import { ContractNature } from "../../../../../autogen/bff-api";
import type { AiMessage } from "../../../../../common/ai/AiDraftModal";

export type DescriptionPromptInput = {
  title: string;
  currentDescription: string;
  contractType?: ContractNature;
  cpvCodes: string[];
  estimatedValueNok?: number | null;
  language: "no" | "en";
};

export type PrivateDescriptionPromptInput = {
  title: string;
  currentDescription: string;
  contractType?: ContractNature;
  estimatedValueNok?: number | null;
  language: "no" | "en";
};

const CONTRACT_TYPE_GLOSS: Record<ContractNature, string> = {
  [ContractNature.Supplies]: "Supplies (purchase of goods or products)",
  [ContractNature.Services]: "Services",
  [ContractNature.Works]: "Works (building and construction)",
  [ContractNature.Combined]: "Combined (a mix of supplies, services and works)",
};

const WRITING_RULES = [
  "Use Markdown: a short intro paragraph, then optionally subheadings and bullet lists for scope and key requirements.",
  "Cover the purpose and background, what is being procured, and the main requirements at a high level.",
  "Be concise and concrete, but do NOT invent specifics that were not provided — no fabricated dates, budgets, brand names, volumes or legal references.",
  "If a current description is provided, improve and build on it rather than discarding it.",
  "Output only the description itself, with no preamble or closing remarks.",
];

const outputLanguage = (language: "no" | "en") => (language === "no" ? "Norwegian (Bokmål)" : "English");

const contractTypeLine = (contractType?: ContractNature) =>
  `Contract type: ${contractType ? CONTRACT_TYPE_GLOSS[contractType] : "(unspecified)"}`;

const estimatedValueLine = (estimatedValueNok?: number | null) =>
  `Estimated value: ${estimatedValueNok != null ? `${estimatedValueNok} NOK` : "(unspecified)"}`;

export const buildDescriptionMessages = ({
  title,
  currentDescription,
  contractType,
  cpvCodes,
  estimatedValueNok,
  language,
}: DescriptionPromptInput): AiMessage[] => {
  return [
    {
      role: "SYSTEM",
      content: [
        "You are an assistant for Norwegian public procurement helping a buyer write a procurement's description (beskrivelse).",
        `Write the description in ${outputLanguage(language)}.`,
        ...WRITING_RULES,
      ].join(" "),
    },
    {
      role: "USER",
      content: [
        `Title: ${title || "(no title)"}`,
        contractTypeLine(contractType),
        `CPV codes: ${cpvCodes.length ? cpvCodes.join(", ") : "(none selected)"}`,
        estimatedValueLine(estimatedValueNok),
        `Current description:\n${currentDescription || "(empty)"}`,
      ].join("\n\n"),
    },
  ];
};

export const buildPrivateDescriptionMessages = ({
  title,
  currentDescription,
  contractType,
  estimatedValueNok,
  language,
}: PrivateDescriptionPromptInput): AiMessage[] => {
  return [
    {
      role: "SYSTEM",
      content: [
        "You are an assistant helping a buyer write the description (beskrivelse) of a private procurement — a closed competition where the buyer invites the suppliers it wants offers from.",
        "It is not a public tender, so do not reference public procurement legislation, notices or Doffin.",
        `Write the description in ${outputLanguage(language)}.`,
        ...WRITING_RULES,
      ].join(" "),
    },
    {
      role: "USER",
      content: [
        `Title: ${title || "(no title)"}`,
        contractTypeLine(contractType),
        estimatedValueLine(estimatedValueNok),
        `Current description:\n${currentDescription || "(empty)"}`,
      ].join("\n\n"),
    },
  ];
};
